import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Lock,
  LogOut,
  Trash2,
  ChevronDown,
  CreditCard,
  Calendar,
  ExternalLink,
  ImagePlus,
  Sun,
  Moon,
} from 'lucide-react';
import { authApi, usersApi } from '../lib/api';
import { useAuthStore, useSettingsStore } from '../lib/store';
import { useMockMode } from '../lib/mockData';

export default function SettingsPage() {
  const navigate = useNavigate();
  const isMock = useMockMode();
  const { logout } = useAuthStore();

  const { themePreference, setThemePreference } = useSettingsStore();

  const [profilOpen, setProfilOpen] = useState(true);
  const [compteOpen, setCompteOpen] = useState(false);
  const [paramsOpen, setParamsOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Mock subscription data
  const subscription = {
    plan: 'Premium',
    nextBilling: '2026-03-07',
    price: '9,99',
    currency: 'EUR',
    interval: 'mois',
  };

  const handleLogout = async () => {
    if (!isMock) { try { await authApi.logout(); } catch {} }
    logout();
    navigate('/');
  };

  const handleDeleteAccount = async () => {
    if (!isMock) {
      try { await usersApi.deleteAccount(''); } catch {}
    }
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen flex flex-col safe-area-inset relative">
      {/* Header */}
      <header className="flex items-center gap-4 px-6 pt-4 pb-6">
        <button onClick={() => navigate(-1)} className="text-white/40 hover:text-white/70 transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
      </header>

      <main className="flex-1 px-5 pb-28 overflow-y-auto space-y-4">
        {/* Profil accordion */}
        <div className="glass-card overflow-hidden">
          <button
            onClick={() => setProfilOpen(!profilOpen)}
            className="w-full flex items-center justify-between p-5"
          >
            <span className="text-sm font-medium text-white/70">Profil</span>
            <motion.div
              animate={{ rotate: profilOpen ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDown className="w-5 h-5 text-white/30" />
            </motion.div>
          </button>

          <AnimatePresence>
            {profilOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                <div className="px-5 pb-5 space-y-2">
                  {/* Divider */}
                  <div className="h-px" style={{ background: 'rgba(255,255,255,0.08)' }} />

                  {/* Change PIN */}
                  <button
                    onClick={() => {/* TODO: change PIN modal */}}
                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/[0.04] transition-colors"
                  >
                    <Lock className="w-5 h-5 text-white/30" />
                    <span className="text-sm text-white/60">Changer le code PIN</span>
                  </button>

                  {/* Logout */}
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/[0.04] transition-colors"
                  >
                    <LogOut className="w-5 h-5 text-white/30" />
                    <span className="text-sm text-white/60">Se deconnecter</span>
                  </button>

                  {/* Divider */}
                  <div className="h-px" style={{ background: 'rgba(255,255,255,0.08)' }} />

                  {/* Mes photos */}
                  <button
                    onClick={() => navigate('/photos')}
                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/[0.04] transition-colors"
                  >
                    <ImagePlus className="w-5 h-5 text-white/30" />
                    <span className="text-sm text-white/60">Gérer mes photos</span>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Compte accordion */}
        <div className="glass-card overflow-hidden">
          <button
            onClick={() => setCompteOpen(!compteOpen)}
            className="w-full flex items-center justify-between p-5"
          >
            <span className="text-sm font-medium text-white/70">Mon compte</span>
            <motion.div
              animate={{ rotate: compteOpen ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDown className="w-5 h-5 text-white/30" />
            </motion.div>
          </button>

          <AnimatePresence>
            {compteOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                <div className="px-5 pb-5 space-y-3">
                  {/* Divider */}
                  <div className="h-px" style={{ background: 'rgba(255,255,255,0.08)' }} />

                  {/* Plan type */}
                  <div className="flex items-center justify-between p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)' }}>
                    <div className="flex items-center gap-3">
                      <CreditCard className="w-5 h-5 text-white/30" />
                      <div>
                        <p className="text-sm text-white/80 font-medium">{subscription.plan}</p>
                        <p className="text-xs text-ivory-faint">{subscription.price} {subscription.currency}/{subscription.interval}</p>
                      </div>
                    </div>
                    <span className="pill-active text-[10px]">Actif</span>
                  </div>

                  {/* Next billing */}
                  <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)' }}>
                    <Calendar className="w-5 h-5 text-white/30" />
                    <div>
                      <p className="text-xs text-white/50">Prochain prelevement</p>
                      <p className="text-sm text-white/80 font-medium">
                        {new Date(subscription.nextBilling).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </p>
                    </div>
                  </div>

                  {/* Change plan */}
                  <button
                    onClick={() => {
                      if (isMock) return;
                      window.open('https://sublym.org/account/subscription', '_blank');
                    }}
                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/[0.04] transition-colors"
                  >
                    <ExternalLink className="w-5 h-5 text-white/30" />
                    <span className="text-sm text-white/60">Changer d'abonnement</span>
                  </button>

                  {/* Divider */}
                  <div className="h-px" style={{ background: 'rgba(255,255,255,0.06)' }} />

                  {/* Delete account */}
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-red-500/[0.04] transition-colors"
                  >
                    <Trash2 className="w-5 h-5 text-red-400/40" />
                    <span className="text-sm text-red-400/60">Supprimer mon compte</span>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Apparence accordion */}
        <div className="glass-card overflow-hidden">
          <button
            onClick={() => setParamsOpen(!paramsOpen)}
            className="w-full flex items-center justify-between p-5"
          >
            <span className="text-sm font-medium text-white/70">Apparence</span>
            <motion.div
              animate={{ rotate: paramsOpen ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDown className="w-5 h-5 text-white/30" />
            </motion.div>
          </button>

          <AnimatePresence>
            {paramsOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                <div className="px-5 pb-5 space-y-4">
                  <div className="h-px" style={{ background: 'rgba(255,255,255,0.08)' }} />

                  {/* Light / Dark */}
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { pref: 'light' as const, icon: Sun, label: 'Clair' },
                      { pref: 'dark' as const, icon: Moon, label: 'Sombre' },
                    ].map((item) => (
                      <button
                        key={item.pref}
                        onClick={() => setThemePreference(item.pref)}
                        className="p-4 rounded-xl flex flex-col items-center gap-2 transition-all duration-200"
                        style={{
                          background: themePreference === item.pref
                            ? 'rgba(255,255,255,0.08)'
                            : 'rgba(255,255,255,0.03)',
                          border: themePreference === item.pref
                            ? '1px solid rgba(255,255,255,0.25)'
                            : '1px solid transparent',
                        }}
                      >
                        <item.icon className={`w-5 h-5 ${themePreference === item.pref ? 'text-white/90' : 'text-white/35'}`} />
                        <span className={`text-xs ${themePreference === item.pref ? 'text-white/70' : 'text-white/30'}`}>{item.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <p className="text-center text-xs text-ivory-faint pb-4 pt-6">
          Sublym Dream v1.0
        </p>
      </main>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowDeleteConfirm(false)} />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass-card-elevated p-6 w-full max-w-sm relative z-10 space-y-4"
          >
            <h3 className="text-lg font-display font-bold text-center">Supprimer le compte ?</h3>
            <p className="text-sm text-ivory-muted text-center">
              Cette action est irreversible. Toutes vos donnees, reves et images seront definitivement supprimes.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 btn-glass py-3 text-sm"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteAccount}
                className="flex-1 py-3 rounded-xl text-sm font-medium transition-colors"
                style={{ background: 'rgba(239,68,68,0.15)', color: '#f87171', border: '1px solid rgba(239,68,68,0.2)' }}
              >
                Supprimer
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
